<template>
    <div class="communications">
        <div class="panel-heading">
            <h3>Recent Communications</h3>
            <div v-if="!isLoading">
                <b-dropdown
                    right
                    size="sm"
                    no-caret
                    toggle-class="btn plus"
                    v-if="_hasAccess('patient.communication.create')"
                    lazy
                >
                    <template slot="button-content">
                        <span>&#43;</span>
                    </template>

                    <b-dropdown-item @click.prevent="logCommunication">
                        <i class="cus-add"></i> Log Communication
                    </b-dropdown-item>
                    <b-dropdown-item @click.prevent="sendEmail">
                        <i class="cus-email"></i> Send Email
                    </b-dropdown-item>
                    <b-dropdown-item
                        @click.prevent="sendSMS"
                        v-if="
                            _isExtensionEnabled('SMS') ||
                            _isExtensionEnabled('2_WAY_MESSAGING')
                        "
                    >
                        <i class="cus-phone"></i> Send SMS
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <loader v-if="isLoading" />

        <div v-else>
            <div v-if="items.length > 0">
                <div class="subheader">
                    <span>Communication:</span>
                    <span>Added:</span>
                </div>

                <div
                    v-for="item in items"
                    v-bind:key="item.id"
                    class="item-row"
                >
                    <input type="hidden" name="id" :value="item.id" />
                    <div>
                        <span class="comm-title">
                            {{ displayCommType(item.type) }} with
                            {{ displayReceiverName(item) }}</span
                        >
                        <br />
                        <div v-html="item.raw_content"></div>
                    </div>
                    <span class="date">{{ properDate(item.date) }}</span>
                    <b-dropdown
                        right
                        size="sm"
                        no-caret
                        toggle-class="options-toggle"
                        lazy
                    >
                        <template slot="button-content">
                            <span class="more">&#183;&#183;&#183;</span>
                        </template>

                        <b-dropdown-item
                            @click.prevent="editCommunication(item)"
                            v-if="
                                item.is_editable &&
                                item.is_editable.status &&
                                item.iseditable === 1 &&
                                _hasAccess('patient.communication.update')
                            "
                        >
                            <i class="cus-page-white-edit"></i> Edit
                            Communication
                        </b-dropdown-item>
                        <b-dropdown-item
                            @click.prevent="deleteCommunication(item)"
                            v-if="_hasAccess('patient.communication.delete')"
                        >
                            <i class="cus-delete"></i> Delete Communication
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
            <div v-else>
                <div class="subheader">
                    <span>Communication:</span>
                    <span>Added:</span>
                </div>

                <p style="margin: 5px 0;">No records available.</p>
            </div>
        </div>

        <modal-delete
            ref="modalDelete"
            module="communication"
            title="Delete Communication"
            @onsuccess="onSuccess()"
        >
            Deleting this communication will also delete the communication
            record created for the recipient.
        </modal-delete>

        <modal-send-email
            ref="modalSendEmail"
            @onsuccess="onSuccess"
            :patientDetails="patientDetails"
        />
        <modal-send-sms
            ref="modalSendSms"
            @onsuccess="onSuccess"
            :patientDetails="patientDetails"
        />
        <modal-log-communication
            ref="modalLogCommunication"
            @onsuccess="onSuccess"
            :patientDetails="patientDetails"
        />
    </div>
</template>

<script>
/* eslint-disable no-undef */
import axios from "axios";
import { DateTime } from "luxon";
import ModalDelete from "./modal-delete";
import ModalSendEmail from "@/components/commons/modal-patient-send-email";
import ModalSendSms from "@/components/commons/modal-patient-send-sms";
import ModalLogCommunication from "@/components/commons/modal-patient-log-communication";

export default {
    components: {
        ModalDelete,
        ModalSendEmail,
        ModalSendSms,
        ModalLogCommunication,
        Loader: () => import("@/components/elements/loader-list")
    },
    props: {
        patientId: {
            type: String,
            required: true
        },
        patientDetails: {
            type: Object
        },
        loadingPermissions: {
            type: Boolean,
            default: true
        },
        isSuperUser: {
            type: Boolean,
            default: false
        },
        currentBusinessId: {
            type: String
        },
        skipFetchData: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            isLoading: true,
            items: [],
            permissions: {}
        };
    },
    mounted: function () {
        if (this.skipFetchData) {
            this.isLoading = false;
        } else {
            this.fetchData();
        }
    },
    methods: {
        sendSMS() {
            this.$refs.modalSendSms.open();
        },
        sendEmail() {
            this.$refs.modalSendEmail.open();
        },
        editCommunication(record) {
            this.$refs.modalLogCommunication.open({
                action: "edit",
                record: record
            });
        },
        logCommunication() {
            this.$refs.modalLogCommunication.open();
        },
        onSuccess() {
            this.$store.dispatch("patient/getHistoryCount", {
                patientId: this.patientId
            });
            this.fetchData();
        },
        fetchData: async function () {
            this.isLoading = true;

            const res = await axios.get(
                process.env.BARRAMUNDI +
                    "communication/patient-communication/" +
                    this.patientId +
                    "?page=1&exclude_system_sent=1"
            );

            this.isLoading = false;

            if (res.data.response.message.code === 200) {
                var records = [];
                var MAX_RECORD = 3;

                // take only top 3
                for (var n = 0; n < res.data.response.resources.length; n++) {
                    if (n >= MAX_RECORD) {
                        break;
                    }

                    var rec = res.data.response.resources[n];
                    records.push(rec);
                }

                this.items = records;
                this.permissions = res.data.response.permissions;
            }
        },
        properDate(date = "") {
            return DateTime.fromISO(date)
                .setZone(this._timeZone)
                .toFormat(this._getLuxonFormat());
        },
        deleteCommunication(record) {
            this.$refs.modalDelete.open(record.id);
        },
        proceedDelete() {},
        addNewCommunication: function (type) {
            var mapping = {
                LOG: {
                    fn: () => COMMUNICATION.add(1),
                    onsave: `COMMUNICATION.save('patient_snapshot')`
                },
                EMAIL: {
                    fn: () => communication.email(1),
                    onsave: `communication.send_email(1, null, 'patient_snapshot')`
                },
                SMS: {
                    fn: () => communication.sms(1),
                    onsave: `communication.send_sms(1, null, null, 'patient_snapshot')`
                }
            };

            if (mapping[type]) {
                $("#modal").one("shown", function () {
                    $("#modal .modal-footer a.btn-primary").attr(
                        "onclick",
                        mapping[type].onsave
                    );
                });

                mapping[type].fn(); // call fn
            }
        },

        displayReceiverName: function (record) {
            let names = [];
            let clients = record.client || [];
            let contacts = record.contact || [];

            for (const client of clients) {
                names.push(client.full_name);
            }

            for (const contact of contacts) {
                names.push(contact.full_name);
            }

            if (names.length > 0) {
                return names.join(", ");
            }

            return "-";
        },

        displayCommType: function (type) {
            if (type) {
                return type.label;
            }

            return "Communication ";
        }
    }
};
</script>

<style scoped>
.communications {
    margin-bottom: 50px;
}
.panel-heading {
    display: grid;
    grid-template-columns: 1fr 28px;
    background-color: transparent;
    align-items: center;
    margin-bottom: 10px;
}
.panel-heading h3 {
    margin: 0;
    font-size: 1.3em;
}
.subheader {
    display: grid;
    grid-template-columns: 1fr 100px 28px;
    column-gap: 5px;
}
.subheader span {
    font-weight: bold;
}
.item-row {
    display: grid;
    grid-template-columns: 1fr 100px 28px;
    border-bottom: 1px solid #ececec;
    padding: 10px 0;
    column-gap: 5px;
    align-items: flex-start;
}
.item-row .btn.more {
    color: #333;
}
.item-row ul.nav-pills li a {
    padding: 2px 0;
}
.comm-title {
    font-weight: bold;
    font-style: italic;
}
a.btn.plus {
    padding: 4px;
}
a.btn.plus,
a.btn.more {
    border-radius: 0;
}
span.date {
    display: block;
    margin-top: 6px;
}
::v-deep .btn.plus {
    border-radius: 0;
    font-weight: bold;
    font-size: 1.1em;
}
::v-deep .options-toggle {
    padding: 0;
    border-radius: 0 !important;
    margin-top: 5px;
}
.more {
    font-weight: bold;
    font-size: 1.1em;
}
</style>
